"use client";

/**
 * Ink snackbar, option A (Leo, 10-16). Spec: design-archive/notifications/design.md v2.
 * One row: mono status label, message, one action, close. Ink and paper flip in `.dark`.
 */

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

export type ToastIntent =
  | "progress"
  | "success"
  | "successUndoable"
  | "warning"
  | "error"
  | "info";

const ToastProvider = ToastPrimitives.Provider;

/** The one region. Bottom center; phone is full width inside 16px gutters, above the safe area. */
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "pointer-events-none fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-[100] m-0 flex list-none flex-col items-center p-0 sm:bottom-6",
      className,
    )}
    label="Notifications"
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

/** Announced before the message so status never rides on the label alone. */
export const INTENT_SR_PREFIX: Record<ToastIntent, string> = {
  progress: "In progress:",
  success: "Success:",
  successUndoable: "Success:",
  warning: "Warning:",
  error: "Error:",
  info: "Info:",
};

const INTENT_LABEL: Record<ToastIntent, string> = {
  progress: "WORKING",
  success: "DONE",
  successUndoable: "DONE",
  warning: "CHECK",
  error: "NOT SAVED",
  info: "UPDATE",
};

/** 2px line draining over `ms`; Web Animations so hover pause freezes it where it is. */
const TimerLine = ({ ms, paused }: { ms: number; paused: boolean }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const anim = React.useRef<Animation | null>(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return undefined;
    anim.current = el.animate(
      [{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }],
      { duration: ms, easing: "linear", fill: "forwards" },
    );
    return () => anim.current?.cancel();
  }, [ms]);
  React.useEffect(() => {
    if (paused) anim.current?.pause();
    else anim.current?.play();
  }, [paused]);
  return (
    <span
      ref={ref}
      data-toast-timer=""
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-0.5 origin-left rounded-b-md bg-teal-border"
    />
  );
};

export interface IntentToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> {
  intent: ToastIntent;
  heading: React.ReactNode;
  body?: React.ReactNode;
  action?: React.ReactNode;
  /** Bars waiting behind this one: ink edges plus "N more waiting". */
  waiting?: number;
  /** Timed bars draw the drain line; omitted for errors and progress. */
  timerMs?: number;
  paused?: boolean;
  reducedMotion?: boolean;
}

export const IntentToast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  IntentToastProps
>(
  (
    {
      intent,
      heading,
      body,
      action,
      waiting = 0,
      timerMs,
      paused = false,
      reducedMotion,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isError = intent === "error";
    // An error's cause earns the second line; any other body runs on in the same row.
    const twoLines = isError && body != null;
    return (
      <ToastPrimitives.Root
        ref={ref}
        type={isError ? "foreground" : "background"}
        role={isError ? "alert" : "status"}
        data-intent={intent}
        data-reduced-motion={reducedMotion ? "true" : undefined}
        className={cn(
          "pointer-events-auto relative flex w-full items-center gap-2 rounded-md bg-ink py-2 pl-3 pr-2 font-sans text-[13px] leading-[1.35] text-paper shadow-[0_6px_20px_hsl(var(--ink)/0.18)] sm:w-max sm:max-w-[480px]",
          isError &&
            "shadow-[0_0_0_2px_hsl(var(--paper)),0_0_0_3px_hsl(var(--ink)),0_6px_20px_hsl(var(--ink)/0.18)]",
          // Queue edges: 4px for the second bar, 8px for the third.
          waiting > 0 &&
            "before:absolute before:inset-x-1.5 before:-bottom-1 before:h-1 before:rounded-b-md before:bg-ink-body",
          waiting > 1 &&
            "after:absolute after:inset-x-3 after:-bottom-2 after:h-1 after:rounded-b-md after:bg-ink-mute",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:duration-200",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-[120ms]",
          "data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)]",
          "motion-reduce:!animate-none data-[reduced-motion=true]:!animate-none data-[reduced-motion=true]:!transition-none",
          className,
        )}
        {...props}
      >
        <div
          data-radix-toast-announce-exclude=""
          className={cn(
            "flex min-w-0 flex-1 gap-2",
            twoLines ? "items-start" : "items-center",
          )}
        >
          {intent === "progress" && (
            <span
              aria-hidden="true"
              className="h-[11px] w-[11px] shrink-0 animate-spin rounded-full border-[1.5px] border-current border-r-transparent motion-reduce:animate-none"
            />
          )}
          {isError ? (
            <span className="shrink-0 rounded-[3px] bg-paper px-[5px] py-0.5 font-mono text-[9px] font-medium tracking-[1px] text-ink">
              {INTENT_LABEL.error}
            </span>
          ) : (
            <span
              className={cn(
                "shrink-0 font-mono text-[9px] font-medium tracking-[1px]",
                intent === "success" || intent === "successUndoable"
                  ? "text-teal-border"
                  : "text-paper/70",
              )}
            >
              {INTENT_LABEL[intent]}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span data-toast-line="">
              {heading}
              {body != null && !twoLines && (
                <span className="text-paper/70"> {body}</span>
              )}
            </span>
            {twoLines && (
              <span data-toast-line="" className="block text-paper/70">
                {body}
              </span>
            )}
          </span>
        </div>
        {action}
        {intent !== "progress" && (
          <ToastPrimitives.Close
            aria-label="Dismiss notification"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-paper/60 hover:bg-paper/10 hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-border"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </ToastPrimitives.Close>
        )}
        {timerMs != null && !reducedMotion && (
          <TimerLine ms={timerMs} paused={paused} />
        )}
        {waiting > 0 && (
          <span
            data-radix-toast-announce-exclude=""
            className="absolute left-1/2 top-[calc(100%+14px)] hidden -translate-x-1/2 whitespace-nowrap font-mono text-[9px] font-medium uppercase tracking-[1px] text-ink-mute sm:block"
          >
            {waiting} more waiting
          </span>
        )}
        {children}
      </ToastPrimitives.Root>
    );
  },
);
IntentToast.displayName = "IntentToast";

export { ToastProvider, ToastViewport };
